import { CommonModule } from '@angular/common';
import { Component, input } from '@angular/core';

/**
 * Live, platform-styled preview of how a post will appear on each social
 * provider. Purely presentational — it composes the title, caption and (when
 * enabled) the "Signed by ODDR" line exactly as the backend does, so users can
 * see the result as they type.
 *
 * The card chrome (avatars, action rows, etc.) is an approximation of each
 * platform's real look, not a pixel-perfect clone.
 */
@Component({
  selector: 'pp-post-preview',
  templateUrl: './post-preview.component.html',
  styleUrl: './post-preview.component.scss',
  imports: [CommonModule],
})
export class PostPreviewComponent {

  /** Provider key: instagram | x | linkedin | facebook | youtube */
  readonly provider = input.required<string>();
  readonly title = input<string | null>();
  readonly caption = input<string | null>();
  readonly includeOddrLink = input(true);
  readonly oddrLink = input<string | null>();
  readonly thumbnailUrl = input<string | null>();

  /** Display name / handle for the mock account row. */
  readonly handle = input<string | null>();
  readonly mediaType = input<'image' | 'video'>('image');

  /** Number of images in the post (>1 renders a carousel indicator). */
  readonly imageCount = input(1);

  /** Account handle shown in the card header, with a friendly fallback. */
  get displayHandle(): string {
    const trimmed = this.handle()?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : 'your_handle';
  }

  /** Trimmed title, or empty string when blank/whitespace. */
  get titleText(): string {
    return this.title()?.trim() ?? '';
  }

  /** Trimmed caption/description, or empty string when blank/whitespace. */
  get captionText(): string {
    return this.caption()?.trim() ?? '';
  }

  get hasTitle(): boolean {
    return this.titleText.length > 0;
  }

  get hasCaption(): boolean {
    return this.captionText.length > 0;
  }

  /** The ODDR line is shown only when enabled and a link is available. */
  get showOddrLink(): boolean {
    return this.includeOddrLink() && !!this.oddrLink();
  }

  /** ODDR link without the protocol, for a compact display. */
  get oddrLinkDisplay(): string {
    return (this.oddrLink() ?? '').replace(/^https?:\/\//, '');
  }

  /** True when there is any user content to show yet. */
  get isEmpty(): boolean {
    return !this.hasTitle && !this.hasCaption;
  }

  get isCarousel(): boolean {
    return this.imageCount() > 1;
  }
}
